import { LayoutTemplate } from "../LayoutTemplate";
import { LineChart } from "@mui/x-charts";
import { useHome } from "./useHome";
import { maskMoney } from "../../../utils/masks";
import { reverseToBrDate } from "../../../utils/dateTransform";
import {
  Breakdown,
  ChartLegend,
  ReportFilters,
  card,
  chartSx,
  num,
  pct,
  pctLabel,
} from "./ui";

export const GeneralTemplate = () => {
  const {
    control,
    handleSubmit,
    setValue,
    watch,
    dataFilter,
    downloadExcelReport,
    dataGeral,
    axisLinear,
    dataLinear,
    CidadeOptions,
    GraphColors,
    handleFilter,
  } = useHome();

  const total = dataGeral?.totalVistorias || 0;
  const revistorias = dataGeral?.revistorias || 0;
  const lojas = dataGeral?.lojas || [];
  const sum = (key: string) =>
    lojas.reduce((acc, item) => acc + (item[key] || 0), 0);
  const sumTotal = sum("total");
  const sumRevistorias = sum("revistorias");

  return (
    <LayoutTemplate title="Relatórios de Produção Geral">
      <div className="flex flex-col gap-5 text-[#1D1D1B]">
        <span className="text-sm text-[#6B6B66]">
          {reverseToBrDate(dataFilter.dataInicio)} –{" "}
          {reverseToBrDate(dataFilter.dataFim)} ·{" "}
          {dataFilter.empresa || "Todas as empresas"}
        </span>

        <ReportFilters
          control={control}
          setValue={setValue}
          watch={watch}
          onSubmit={handleSubmit(handleFilter)}
          options={CidadeOptions}
          colors={GraphColors}
        />

        {/* KPIs principais */}
        <section
          aria-label="Indicadores principais"
          className="grid grid-cols-[repeat(auto-fit,minmax(220px,1fr))] gap-4"
        >
          <div className="flex flex-col gap-3.5 rounded-2xl bg-[#1D1D1B] p-5 text-white">
            <span className="text-[13px] font-medium text-[#C9C8C2]">
              Total de vistorias
            </span>
            <span className="text-4xl font-bold tabular-nums tracking-tight">
              {num(total)}
            </span>
            <span className="text-[13px] text-[#C9C8C2]">
              no período selecionado
            </span>
          </div>
          {[
            {
              title: "Vistorias líquidas",
              value: num(total > 0 ? total - revistorias : 0),
              hint: "total − revistorias",
            },
            {
              title: "Ticket médio",
              value: maskMoney(dataGeral?.ticketMedio),
              hint: "por vistoria",
            },
          ].map((k) => (
            <div key={k.title} className={`${card} flex flex-col gap-3.5`}>
              <span className="text-[13px] font-medium text-[#6B6B66]">
                {k.title}
              </span>
              <span className="text-4xl font-bold tabular-nums tracking-tight">
                {k.value}
              </span>
              <span className="text-[13px] text-[#6B6B66]">{k.hint}</span>
            </div>
          ))}
          <div className={`${card} flex flex-col gap-3.5`}>
            <span className="text-[13px] font-medium text-[#6B6B66]">
              Revistorias
            </span>
            <span className="text-4xl font-bold tabular-nums tracking-tight">
              {num(revistorias)}
            </span>
            <span className="self-start rounded-full bg-[#FDEDE6] px-2 py-0.5 text-xs font-semibold text-[#B83A12]">
              {pctLabel(pct(revistorias, total))} do total
            </span>
          </div>
        </section>

        {/* Composição */}
        <section
          aria-label="Composição"
          className="grid grid-cols-[repeat(auto-fit,minmax(300px,1fr))] gap-4"
        >
          <Breakdown
            title="Canal de atendimento"
            parts={[
              { label: "Loja", value: dataGeral?.totalLoja, color: "#1D1D1B" },
              { label: "Móvel", value: dataGeral?.totalMovel, color: "#E84E1B" },
              {
                label: "Itinerante",
                value: dataGeral?.totalItinerante,
                color: "#F4A988",
              },
            ]}
          />
          <Breakdown
            title="Valor da vistoria"
            parts={[
              { label: "R$ 152,00", value: dataGeral?.qtd152, color: "#E84E1B" },
              { label: "R$ 190,00", value: dataGeral?.qtd190, color: "#3C3C3B" },
            ]}
          />
          <Breakdown
            title="Tipo de serviço"
            parts={[
              {
                label: "Primeiro emplacamento",
                value: dataGeral?.qtdPrimeiroEmplacamento,
                color: "#E84E1B",
              },
              {
                label: "Transferência",
                value: dataGeral?.qtdTransferencia,
                color: "#3C3C3B",
              },
            ]}
          />
        </section>

        {/* Gráficos */}
        <section className="flex flex-wrap gap-4">
          <div className={`${card} flex min-w-0 flex-[2_1_560px] flex-col gap-4`}>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex flex-col gap-1">
                <h2 className="text-base font-semibold">
                  Produção diária de vistorias
                </h2>
                <span className="text-[13px] text-[#6B6B66]">
                  Vistorias por dia, por empresa
                </span>
              </div>
              <ChartLegend series={dataLinear} />
            </div>
            <LineChart
              height={300}
              margin={{ top: 16, right: 16, bottom: 30, left: 44 }}
              series={dataLinear}
              xAxis={[{ scaleType: "band", data: axisLinear }]}
              // Eixo sempre a partir de 0: evita que um valor baixo pareça produção zerada
              yAxis={[{ min: 0, tickMinStep: 1 }]}
              slotProps={{ legend: { hidden: true } }}
              sx={chartSx}
            />
          </div>

          <div className={`${card} flex min-w-0 flex-[1_1_340px] flex-col gap-[18px]`}>
            <div className="flex flex-col gap-1">
              <h2 className="text-base font-semibold">
                Mix de valor por empresa
              </h2>
              <span className="text-[13px] text-[#6B6B66]">
                % de vistorias R$ 152,00 vs R$ 190,00
              </span>
            </div>
            <div className="flex gap-4 text-[13px] text-[#4A4A46]">
              <span className="inline-flex items-center gap-1.5">
                <span className="h-2.5 w-2.5 rounded-[3px] bg-[#E84E1B]" />
                R$ 152,00
              </span>
              <span className="inline-flex items-center gap-1.5">
                <span className="h-2.5 w-2.5 rounded-[3px] bg-[#3C3C3B]" />
                R$ 190,00
              </span>
            </div>
            <div className="flex flex-col gap-[18px]">
              {dataGeral?.empresas?.map((item) => (
                <div key={item.empresa} className="flex flex-col gap-2">
                  <div className="flex justify-between text-sm">
                    <span className="inline-flex items-center gap-2 font-medium">
                      <span
                        className="h-2 w-2 rounded-full"
                        style={{ background: GraphColors[item.empresa] }}
                      />
                      {item.empresa}
                    </span>
                    <span className="tabular-nums text-[#6B6B66]">
                      {item.per152}% · {item.per190}%
                    </span>
                  </div>
                  <div className="flex h-[22px] gap-0.5 overflow-hidden rounded-md bg-[#F1F0EC] text-[11px] font-semibold tabular-nums text-white">
                    {item.per152 > 0 && (
                      <span
                        className="flex items-center bg-[#E84E1B] pl-2"
                        style={{ flexGrow: item.per152 }}
                      >
                        {item.per152}%
                      </span>
                    )}
                    {item.per190 > 0 && (
                      <span
                        className="flex items-center justify-end bg-[#3C3C3B] pr-2"
                        style={{ flexGrow: item.per190 }}
                      >
                        {item.per190}%
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Tabela por unidade */}
        <section className="mb-8 flex flex-col overflow-hidden rounded-2xl border border-[#E7E6E2] bg-white">
          <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-4">
            <div className="flex items-baseline gap-2.5">
              <h2 className="text-base font-semibold">Produção por unidade</h2>
              <span className="text-[13px] text-[#6B6B66]">
                {lojas.length} unidades
              </span>
            </div>
            <button
              type="button"
              onClick={downloadExcelReport}
              className="inline-flex h-11 items-center gap-2 rounded-[10px] border border-[#DAD9D4] bg-white px-[18px] text-sm font-semibold hover:bg-[#FAFAF8]"
            >
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <path d="M7 10l5 5 5-5" />
                <path d="M12 15V3" />
              </svg>
              Exportar XLSX
            </button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[1080px] border-collapse text-sm tabular-nums [&_td]:whitespace-nowrap [&_td]:px-3.5 [&_td]:py-3 [&_th]:whitespace-nowrap [&_th]:px-3.5 [&_th]:py-3">
              <thead>
                <tr className="bg-[#F7F7F5] text-right text-xs uppercase tracking-wider text-[#6B6B66] [&_th]:font-semibold">
                  <th className="text-left">Unidade</th>
                  <th className="text-left">Canal</th>
                  <th>Loja</th>
                  <th>Móvel</th>
                  <th>Itiner.</th>
                  <th>Revist.</th>
                  <th>R$ 152</th>
                  <th>R$ 190</th>
                  <th>Ticket médio</th>
                  <th>Líquido</th>
                  <th>Total</th>
                </tr>
              </thead>
              <tbody>
                {lojas.map((item) => {
                  const canais =
                    (item.qtdLoja || 0) +
                    (item.qtdMovel || 0) +
                    (item.qtdItinerante || 0);
                  return (
                    <tr
                      key={`${item.empresa}-${item.loja}`}
                      className="border-t border-[#EEEDE9] text-right hover:bg-[#FAFAF8]"
                    >
                      <td className="text-left">
                        <div className="flex items-center gap-2.5">
                          <span
                            className="h-8 w-1 rounded-sm"
                            style={{ background: GraphColors[item.empresa] }}
                          />
                          <div className="flex flex-col gap-0.5">
                            <span className="font-semibold">
                              {item.loja || "---"}
                            </span>
                            <span className="text-xs text-[#6B6B66]">
                              {item.empresa || "---"}
                            </span>
                          </div>
                        </div>
                      </td>
                      <td>
                        <div className="flex h-2 w-24 gap-px overflow-hidden rounded-full bg-[#F1F0EC]">
                          <span
                            className="bg-[#1D1D1B]"
                            style={{ width: `${pct(item.qtdLoja, canais)}%` }}
                          />
                          <span
                            className="bg-[#E84E1B]"
                            style={{ width: `${pct(item.qtdMovel, canais)}%` }}
                          />
                          <span
                            className="bg-[#F4A988]"
                            style={{
                              width: `${pct(item.qtdItinerante, canais)}%`,
                            }}
                          />
                        </div>
                      </td>
                      <td>{num(item.qtdLoja)}</td>
                      <td>{num(item.qtdMovel)}</td>
                      <td>{num(item.qtdItinerante)}</td>
                      <td className="text-[#B83A12]">
                        {num(item.revistorias)}
                      </td>
                      <td>{num(item.qtd152)}</td>
                      <td>{num(item.qtd190)}</td>
                      <td>{maskMoney(item.ticketMedio)}</td>
                      <td>
                        {num(item.total > 0 ? item.total - item.revistorias : 0)}
                      </td>
                      <td className="font-bold">{num(item.total)}</td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="border-t-2 border-[#1D1D1B] bg-[#F7F7F5] text-right font-bold">
                  <td className="text-left">Total geral</td>
                  <td />
                  <td>{num(sum("qtdLoja"))}</td>
                  <td>{num(sum("qtdMovel"))}</td>
                  <td>{num(sum("qtdItinerante"))}</td>
                  <td className="text-[#B83A12]">{num(sumRevistorias)}</td>
                  <td>{num(sum("qtd152"))}</td>
                  <td>{num(sum("qtd190"))}</td>
                  <td>{maskMoney(dataGeral?.ticketMedio)}</td>
                  <td>{num(sumTotal > 0 ? sumTotal - sumRevistorias : 0)}</td>
                  <td>{num(sumTotal)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
          <div className="flex gap-4 border-t border-[#EEEDE9] px-5 py-3 text-xs text-[#6B6B66]">
            {[
              ["Loja", "#1D1D1B"],
              ["Móvel", "#E84E1B"],
              ["Itinerante", "#F4A988"],
            ].map(([label, color]) => (
              <span key={label} className="inline-flex items-center gap-1.5">
                <span
                  className="h-2 w-2.5 rounded-sm"
                  style={{ background: color }}
                />
                {label}
              </span>
            ))}
          </div>
        </section>
      </div>
    </LayoutTemplate>
  );
};
